import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { getMandateMatchOverride, saveMandateMatchOverride } from '../../api/saleMandates';
import type { MatchSettings } from '../../api/settings';

interface Props {
  mandateId: string;
  onClose: () => void;
  onSaved: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: '13px', background: 'var(--surface)', color: 'var(--text)',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: '4px',
};

/** Felder die per Mandate überschreibbar sind (Auswahl der wichtigsten). */
const OVERRIDE_FIELDS: Array<{
  key: keyof MatchSettings;
  label: string;
  type: 'select' | 'number';
  options?: { value: string; label: string }[];
  min?: number; max?: number;
}> = [
  { key: 'country_mode', label: 'Land-Filter', type: 'select', options: [
    { value: 'hard', label: 'Hart' }, { value: 'soft', label: 'Weich' }, { value: 'off', label: 'Aus' },
  ] },
  { key: 'city_mode', label: 'Stadt-Match', type: 'select', options: [
    { value: 'fuzzy', label: 'Fuzzy' }, { value: 'strict', label: 'Strict' }, { value: 'off', label: 'Aus' },
  ] },
  { key: 'volume_tolerance_pct', label: 'Volumen-Toleranz %', type: 'number', min: 0, max: 500 },
  { key: 'size_tolerance_pct', label: 'Fläche-Toleranz %', type: 'number', min: 0, max: 500 },
  { key: 'min_score', label: 'Cutoff (min. Score)', type: 'number', min: 0, max: 100 },
];

export function MandateMatchOverrideModal({ mandateId, onClose, onSaved }: Props) {
  const [enabled, setEnabled] = useState(false);
  const [systemDefaults, setSystemDefaults] = useState<MatchSettings | null>(null);
  const [override, setOverride] = useState<Partial<MatchSettings>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    getMandateMatchOverride(mandateId).then(r => {
      setSystemDefaults(r.system_defaults);
      if (r.override) {
        setEnabled(true);
        setOverride(r.override);
      }
    }).catch(() => setMsg('Fehler beim Laden'));
  }, [mandateId]);

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      await saveMandateMatchOverride(mandateId, enabled ? override : null);
      onSaved();
    } catch { setMsg('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const updateField = (key: keyof MatchSettings, raw: string) => {
    const field = OVERRIDE_FIELDS.find(f => f.key === key);
    if (!field) return;
    const value: unknown = field.type === 'number' ? parseInt(raw || '0') : raw;
    setOverride(prev => ({ ...prev, [key]: value }));
  };

  if (!systemDefaults) {
    return <Modal isOpen={true} onClose={onClose} title="Such-Einstellungen für Mandat" size="md">
      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
    </Modal>;
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Such-Einstellungen für dieses Mandat" size="md">
      <div style={{ padding: '0 4px' }}>
        <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text3)' }}>
          Standardmäßig nutzt jedes Mandat die System-Einstellungen aus den globalen Settings.
          Hier kannst du für dieses eine Mandat einzelne Werte überschreiben.
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16, padding: '10px 14px', background: enabled ? 'var(--accent-light)' : 'var(--surface2)', borderRadius: 'var(--radius)', border: enabled ? '1px solid var(--accent-mid)' : '1px solid var(--border)' }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          <span style={{ fontWeight: 500 }}>Eigene Such-Einstellungen für dieses Mandat aktivieren</span>
        </label>

        {enabled && (
          <div style={{ display: 'grid', gap: 12, opacity: enabled ? 1 : 0.5, pointerEvents: enabled ? 'auto' : 'none' }}>
            {OVERRIDE_FIELDS.map(f => {
              const overrideVal = override[f.key];
              const sysVal = systemDefaults[f.key];
              const effective = overrideVal !== undefined ? overrideVal : sysVal;
              const isOverridden = overrideVal !== undefined;
              return (
                <div key={String(f.key)}>
                  <label style={labelStyle}>
                    {f.label}
                    {isOverridden && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>● überschrieben</span>}
                    {!isOverridden && <span style={{ color: 'var(--text3)', marginLeft: 6, textTransform: 'none' }}>System-Default: {String(sysVal)}</span>}
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {f.type === 'select' ? (
                      <select style={inputStyle} value={String(effective)}
                        onChange={e => updateField(f.key, e.target.value)}>
                        {f.options!.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input style={inputStyle} type="number" min={f.min} max={f.max}
                        value={String(effective)}
                        onChange={e => updateField(f.key, e.target.value)} />
                    )}
                    {isOverridden && (
                      <button type="button"
                        onClick={() => setOverride(prev => { const n = { ...prev }; delete n[f.key]; return n; })}
                        title="Override entfernen, System-Default verwenden"
                        style={{ padding: '0 10px', fontSize: 12, border: '1px solid var(--border2)', background: 'var(--surface)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text3)' }}>
                        ↺
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {msg && <div style={{ color: 'var(--accent)', fontSize: 12, marginTop: 12 }}>{msg}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 18px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
            Abbrechen
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ padding: '8px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500, cursor: saving ? 'wait' : 'pointer' }}>
            {saving ? 'Wird gespeichert...' : 'Speichern'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
